import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { fmtNum, shortDate, fmtDate } from "@/lib/format";
import {
  Users, GraduationCap, MapPin, Search, Download, AlertCircle,
  CheckCircle2, Clock, XCircle, Phone, User,
} from "@/components/icons/console-icons";

type Student = {
  id: string;
  student_number: string;
  full_name: string;
  date_of_birth: string;
  gender: string;
  grade: string;
  class_name: string;
  enrollment_status: string;
  feeding_eligible: boolean;
  dietary_restrictions: string | null;
  emergency_contact: string | null;
  emergency_phone: string | null;
  school_id: string;
  school?: { name: string; district: string };
  catchment_area_id: string | null;
  catchment_area?: { name: string };
  ward_id: string | null;
  ward?: { name: string; district?: { name: string; province?: { name: string } } };
};

const GENDER_CONFIG: Record<string, { label: string; color: string }> = {
  male: { label: "Male", color: "#3b82f6" },
  female: { label: "Female", color: "#ec4899" },
};

const GRADE_CONFIG: Record<string, { label: string; level: string }> = {
  "Grade 1": { label: "Grade 1", level: "Primary" },
  "Grade 2": { label: "Grade 2", level: "Primary" },
  "Grade 3": { label: "Grade 3", level: "Primary" },
  "Grade 4": { label: "Grade 4", level: "Primary" },
  "Grade 5": { label: "Grade 5", level: "Primary" },
  "Grade 6": { label: "Grade 6", level: "Primary" },
  "Grade 7": { label: "Grade 7", level: "Primary" },
  "Form 1": { label: "Form 1", level: "Secondary" },
  "Form 2": { label: "Form 2", level: "Secondary" },
  "Form 3": { label: "Form 3", level: "Secondary" },
  "Form 4": { label: "Form 4", level: "Secondary" },
};

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export function StudentRegistry() {
  const [search, setSearch] = useState("");
  const [gradeFilter, setGradeFilter] = useState("");
  const [schoolFilter, setSchoolFilter] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 50;

  const { data: students = [], isLoading } = useQuery({
    queryKey: ["zw-student-registry", page, pageSize],
    queryFn: async () => {
      const { data } = await supabase
        .from("zw_students")
        .select(`
          id, student_number, full_name, date_of_birth, gender, grade, class_name,
          enrollment_status, feeding_eligible, dietary_restrictions,
          emergency_contact, emergency_phone, school_id,
          school:schools(name, district),
          catchment_area:zw_catchment_areas(name),
          ward:zw_wards(name, district:zw_districts(name, province:zw_provinces(name)))
        `)
        .eq("enrollment_status", "enrolled")
        .order("full_name")
        .range(page * pageSize, (page + 1) * pageSize - 1);
      return (data ?? []) as unknown as Student[];
    },
  });

  const { data: schools = [] } = useQuery({
    queryKey: ["zw-schools-registry"],
    queryFn: async () => (await supabase.from("schools").select("id, name").order("name").limit(30)).data ?? [],
  });

  const { data: totalStudents } = useQuery({
    queryKey: ["zw-student-registry-count"],
    queryFn: async () => (await supabase.from("zw_students").select("*", { count: "exact", head: true })).count ?? 0,
  });

  const filtered = useMemo(() =>
    students.filter(s => {
      if (search && !s.full_name.toLowerCase().includes(search.toLowerCase()) && !s.student_number.toLowerCase().includes(search.toLowerCase())) return false;
      if (gradeFilter && s.grade !== gradeFilter) return false;
      if (schoolFilter && s.school_id !== schoolFilter) return false;
      return true;
    }),
  [students, search, gradeFilter, schoolFilter]);

  // Grade distribution
  const gradeData = useMemo(() => {
    const m: Record<string, number> = {};
    students.forEach(s => { m[s.grade] = (m[s.grade] ?? 0) + 1; });
    return Object.entries(m).map(([name, count]) => ({ name, count }));
  }, [students]);

  const FS = "'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif";

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse text-muted-foreground">Loading student registry...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      {/* Header */}
      <div className="relative overflow-hidden" style={{ background: "linear-gradient(135deg, #0d6b3d 0%, #0a4a30 50%, #062818 100%)" }}>
        <div className="relative mx-auto max-w-7xl px-4 py-7 md:px-6 md:py-9">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em]" style={{ color: "#d4a017" }}>
            <span className="h-px w-6" style={{ background: "#c96a4a" }} /> BRECCS Zimbabwe · Student Registry
          </div>
          <h1 className="mt-2 text-2xl font-bold text-white md:text-3xl" style={{ fontFamily: FS }}>
            Zimbabwe Student Registry
          </h1>
          <p className="mt-1 text-sm text-white/70">
            {fmtNum(totalStudents ?? 0)} enrolled students across {fmtNum(schools.length)} schools. Complete profiles linked to guardians, schools, catchments, and feeding plans.
          </p>
          <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-4 lg:grid-cols-6">
            {gradeData.slice(0, 6).map(g => (
              <div key={g.name} className="rounded-lg border border-white/10 bg-white/5 px-3 py-2">
                <div className="text-[9px] uppercase tracking-wider text-white/40">{g.name}</div>
                <div className="font-mono text-sm font-bold text-white">{fmtNum(g.count)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-7xl px-4 py-6 md:px-6">
        {/* Filters */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by name or student number..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}
              className="pl-9"
            />
          </div>
          <select
            value={gradeFilter}
            onChange={e => { setGradeFilter(e.target.value); setPage(0); }}
            className="rounded-md border bg-card px-3 py-2 text-sm"
          >
            <option value="">All Grades</option>
            {Object.entries(GRADE_CONFIG).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <select
            value={schoolFilter}
            onChange={e => { setSchoolFilter(e.target.value); setPage(0); }}
            className="rounded-md border bg-card px-3 py-2 text-sm"
          >
            <option value="">All Schools</option>
            {schools.map((s: any) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            <Download className="mr-1 h-4 w-4" /> Export
          </Button>
        </div>

        {/* Student Cards */}
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Users className="h-12 w-12 text-muted-foreground/30 mb-3" />
            <p className="text-sm font-medium text-muted-foreground">No students found</p>
            <p className="text-xs text-muted-foreground/60 mt-1">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map(s => {
              const gc = GENDER_CONFIG[s.gender] ?? { label: s.gender, color: "#667085" };
              return (
                <Card key={s.id} className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-xs font-bold text-white" style={{ background: gc.color }}>
                      {getInitials(s.full_name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold truncate">{s.full_name}</span>
                        <Badge variant="outline" className="text-[9px] shrink-0">{s.student_number}</Badge>
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground">
                        <span className="flex items-center gap-0.5">
                          <GraduationCap className="h-3 w-3" />{s.grade} · {s.class_name}
                        </span>
                        <span className="flex items-center gap-0.5">
                          <MapPin className="h-3 w-3" />{s.school?.name ?? "—"}
                        </span>
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[10px]">
                        <Badge variant="outline" className="text-[9px]" style={{ borderColor: `${gc.color}30`, color: gc.color }}>{gc.label}</Badge>
                        {s.feeding_eligible && (
                          <Badge variant="outline" className="text-[9px] text-emerald-600 border-emerald-200">Feeding: Eligible</Badge>
                        )}
                        {s.dietary_restrictions && (
                          <Badge variant="outline" className="text-[9px] text-amber-600 border-amber-200">{s.dietary_restrictions}</Badge>
                        )}
                      </div>
                      {/* Geographic info */}
                      <div className="mt-1.5 text-[10px] text-muted-foreground/70">
                        {s.ward?.name && <span>Ward: {s.ward.name}</span>}
                        {s.ward?.district?.name && <span> · District: {s.ward.district.name}</span>}
                        {s.ward?.district?.province?.name && <span> · Province: {s.ward.district.province.name}</span>}
                      </div>
                      {s.catchment_area?.name && (
                        <div className="text-[10px] text-muted-foreground/70">Catchment: {s.catchment_area.name}</div>
                      )}
                      {s.emergency_phone && (
                        <div className="mt-1 flex items-center gap-1 text-[10px] text-muted-foreground">
                          <Phone className="h-3 w-3" />{s.emergency_phone}
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalStudents && totalStudents > pageSize && (
          <div className="mt-6 flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage(p => Math.max(0, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page + 1} of {Math.ceil((totalStudents ?? 0) / pageSize)}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={(page + 1) * pageSize >= (totalStudents ?? 0)}
              onClick={() => setPage(p => p + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
